// src/theme.js
import { alpha, createTheme } from '@mui/material/styles';

const LIGHT_TOKENS = {
  primary: '#1d63b8',
  secondary: '#0b7c89',
  backgroundDefault: '#eef3fb',
  backgroundPaper: '#ffffff',
  textPrimary: '#0f172a',
  textSecondary: '#475569'
};

const DARK_TOKENS = {
  primary: '#5ea6ff',
  secondary: '#39c4d5',
  backgroundDefault: '#060f24',
  backgroundPaper: '#111f3a',
  textPrimary: '#eaf2ff',
  textSecondary: '#9eb2d1'
};

const createAppTheme = (mode, primaryColor) => {
  const isDark = mode === 'dark';
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;
  const activePrimary = primaryColor || tokens.primary;

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1200,
        xl: 1440
      }
    },
    palette: {
      mode,
      primary: {
        main: activePrimary,
        contrastText: isDark ? '#061128' : '#f8fbff'
      },
      secondary: {
        main: tokens.secondary
      },
      background: {
        default: tokens.backgroundDefault,
        paper: tokens.backgroundPaper
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary
      },
      divider: isDark ? alpha('#9eb2d1', 0.22) : alpha('#1e293b', 0.14),
      success: { main: '#2fb272' },
      warning: { main: '#e8a222' },
      error: { main: '#e55353' },
      info: { main: '#3f8cff' }
    },
    shape: {
      borderRadius: 12
    },
    typography: {
      fontFamily: '"Manrope", "IBM Plex Sans", "Segoe UI", Roboto, Arial, sans-serif',
      h1: { fontWeight: 800, letterSpacing: '-0.015em' },
      h2: { fontWeight: 750, letterSpacing: '-0.012em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 650 },
      h6: { fontWeight: 650 },
      button: { fontWeight: 700, letterSpacing: '0.01em', textTransform: 'none' }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'hidden'
          },
          body: {
            background: isDark
              ? 'radial-gradient(1200px 500px at 75% -120px, rgba(70,140,255,0.16), transparent 60%), #060f24'
              : 'radial-gradient(1200px 500px at 75% -120px, rgba(29,99,184,0.14), transparent 60%), #eef3fb',
            color: tokens.textPrimary
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${isDark ? alpha('#9eb2d1', 0.25) : alpha('#1e293b', 0.14)}`
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            maxWidth: '100vw',
            boxSizing: 'border-box',
            borderRight: `1px solid ${isDark ? alpha('#9eb2d1', 0.2) : alpha('#1e293b', 0.12)}`,
            '@media (max-width:600px)': {
              paddingBottom: 'env(safe-area-inset-bottom, 0px)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#9eb2d1', 0.18) : alpha('#1e293b', 0.09)}`
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#9eb2d1', 0.18) : alpha('#1e293b', 0.1)}`,
            boxShadow: isDark
              ? '0 10px 30px rgba(3,8,20,0.45)'
              : '0 10px 24px rgba(14,30,64,0.08)'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingInline: 14,
            minHeight: 38,
            maxWidth: '100%',
            whiteSpace: 'normal',
            '@media (max-width:600px)': {
              minHeight: 44
            }
          },
          contained: {
            boxShadow: 'none'
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minWidth: 44,
              minHeight: 44
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minWidth: 0,
            borderRadius: 10,
            backgroundColor: isDark ? alpha('#ffffff', 0.02) : alpha('#ffffff', 0.85),
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha('#9eb2d1', 0.28) : alpha('#1e293b', 0.2)
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha(activePrimary, 0.75) : alpha(activePrimary, 0.65)
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            minWidth: 0,
            maxWidth: '100%',
            '@media (max-width:600px)': {
              minHeight: 44
            }
          },
          input: {
            minWidth: 0,
            '@media (max-width:600px)': {
              fontSize: 16
            }
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            minWidth: 0,
            maxWidth: '100%'
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minWidth: '0 !important',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            minWidth: 0,
            overflowWrap: 'anywhere'
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            overflowWrap: 'anywhere'
          }
        }
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            minWidth: 0
          },
          primary: {
            overflowWrap: 'anywhere'
          },
          secondary: {
            overflowWrap: 'anywhere'
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          message: {
            minWidth: 0,
            overflowWrap: 'anywhere'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 44,
            maxWidth: '100%',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            lineHeight: 1.35
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100dvh - 32px)',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            '@media (max-width:960px), (hover:none) and (pointer:coarse)': {
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'min(56dvh, 480px)'
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            minWidth: 'min(180px, calc(100vw - 24px)) !important',
            width: 'max-content',
            maxWidth: 'min(560px, calc(100vw - 24px)) !important',
            maxHeight: 'min(70dvh, 520px)',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            '@media (max-width:960px), (hover:none) and (pointer:coarse)': {
              maxWidth: 'min(520px, calc(100vw - 24px)) !important',
              maxHeight: 'min(56dvh, 480px)',
              borderRadius: 14
            }
          },
          list: {
            paddingTop: 6,
            paddingBottom: 6
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            minWidth: '0 !important',
            maxWidth: 'min(560px, calc(100vw - 24px)) !important'
          },
          paper: {
            maxWidth: '100%',
            overflowX: 'hidden',
            overscrollBehavior: 'contain'
          },
          listbox: {
            maxHeight: 'min(46dvh, 420px)',
            overflowX: 'hidden'
          },
          option: {
            minHeight: 44,
            minWidth: 0,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            lineHeight: 1.35
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              fontWeight: 750,
              backgroundColor: isDark ? alpha('#ffffff', 0.04) : alpha('#0f172a', 0.04)
            }
          }
        }
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain'
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            maxWidth: '100%',
            minWidth: 0
          },
          scroller: {
            '@media (max-width:600px)': {
              overflowX: 'auto !important',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }
          },
          flexContainer: {
            '@media (max-width:600px)': {
              width: 'max-content',
              minWidth: '100%'
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              minWidth: 'auto',
              maxWidth: 'none',
              flexShrink: 0,
              paddingInline: 12
            }
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 120ms ease'
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            maxWidth: '100%'
          },
          label: {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              padding: 16,
              '&:last-child': {
                paddingBottom: 16
              }
            }
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 48,
            '&.Mui-expanded': {
              minHeight: 48
            }
          },
          content: {
            minWidth: 0,
            '& > *': {
              minWidth: 0
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            maxWidth: 'calc(100vw - 24px)',
            '@media (max-width:600px)': {
              width: '100%',
              maxWidth: '100%',
              height: '100dvh',
              maxHeight: '100dvh',
              margin: 0,
              borderRadius: 0,
              '&.MuiDialog-paperWidthXs': {
                width: 'calc(100% - 24px)',
                height: 'auto',
                maxHeight: 'calc(100dvh - 24px)',
                margin: 12,
                borderRadius: 16
              }
            }
          },
          paperFullScreen: {
            width: '100%',
            maxWidth: '100%',
            height: '100dvh',
            maxHeight: '100dvh',
            margin: 0,
            borderRadius: 0,
            '& > .MuiDialogTitle-root:first-of-type': {
              paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))'
            },
            '& > .MuiDialogActions-root:last-child': {
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))'
            }
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            minWidth: 0,
            overflowWrap: 'anywhere',
            '@media (max-width:600px)': {
              padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 14px'
            }
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            minWidth: 0,
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            '@media (max-width:600px)': {
              paddingLeft: 16,
              paddingRight: 16
            }
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            minWidth: 0,
            '@media (max-width:600px)': {
              flexWrap: 'wrap',
              gap: 8,
              padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
              '& .MuiButton-root + .MuiButton-root': {
                marginLeft: 0
              },
              '& .MuiButton-root': {
                flex: '1 1 132px'
              }
            }
          }
        }
      },
      MuiPagination: {
        styleOverrides: {
          ul: {
            justifyContent: 'center',
            flexWrap: 'wrap',
            rowGap: 4
          }
        }
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '@media (max-width:600px)': {
              left: 12,
              right: 12,
              bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))'
            }
          }
        }
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            maxWidth: '100%',
            overflowWrap: 'anywhere'
          },
          message: {
            minWidth: 0,
            overflowWrap: 'anywhere'
          }
        }
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 0,
            minHeight: 48
          },
          label: {
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        }
      }
    }
  });
};

export default createAppTheme;
